/**
 * The latest report at a glance: four figures, the backup and database block
 * (item J, when there is anything to say), what needs attention, and what
 * changed since the report before it.
 *
 * The attention list is the server's (`reporter/findings.ts`), worst first,
 * and says what the plugin saw, never "vulnerable": matching versions against
 * a vulnerability feed is a later step. The change list puts the alarming
 * lines (a new administrator, a new PHP file) above the routine ones (an
 * update that went through).
 */
import { AlertTriangle, ArrowRight, CircleCheck } from "lucide-react";
import { showBackupBlock } from "../../../../lib/monitoringBackup";
import {
  diffLines,
  findingMessage,
  findingTone,
  type ReporterExpectView,
  type ReporterFinding,
  type ReporterReportView,
} from "../../../../lib/monitoringReporter";
import { StatusPill, fmtDateTime } from "../../../ui";
import type { T } from "../words";
import { BackupBlock } from "./BackupBlock";

function Figures({ report, t }: { report: ReporterReportView; t: T }) {
  const updates = report.plugins.filter((p) => p.update).length;
  const newAdmins = report.admins.baseline ? 0 : report.admins.newLogins.length;
  return (
    <div className="crm-pstats grid-cols-2 lg:grid-cols-4">
      <div className="crm-pstat">
        <b className="crm-tnum">{report.coreVersion}</b>
        <span>WordPress</span>
        <span className={report.coreUpdate ? "crm-pstat-sub text-[var(--color-warning)]" : "crm-pstat-sub"}>
          {report.coreUpdate ? t("monitoring.reporter.fig.updateTo", { version: report.coreUpdate }) : t("monitoring.reporter.fig.current")}
        </span>
      </div>
      <div className="crm-pstat">
        <b className="crm-tnum">{report.phpVersion}</b>
        <span>PHP</span>
        <span className={report.phpEol ? "crm-pstat-sub text-[var(--color-warning)]" : "crm-pstat-sub"}>
          {report.phpEol ? t("monitoring.reporter.fig.eol") : t("monitoring.reporter.fig.supported")}
        </span>
      </div>
      <div className="crm-pstat">
        <b className="crm-tnum">{report.plugins.length}</b>
        <span>{t("monitoring.reporter.fig.plugins")}</span>
        <span className={updates > 0 ? "crm-pstat-sub text-[var(--color-warning)]" : "crm-pstat-sub"}>
          {t("monitoring.reporter.fig.updates", { n: updates })}
        </span>
      </div>
      <div className="crm-pstat">
        <b className="crm-tnum">{report.admins.count}</b>
        <span>{t("monitoring.reporter.fig.admins")}</span>
        <span className={newAdmins > 0 ? "crm-pstat-sub text-[var(--color-destructive)]" : "crm-pstat-sub"}>
          {t("monitoring.reporter.fig.newAdmins", { n: newAdmins })}
        </span>
      </div>
    </div>
  );
}

function findingText(t: T, f: ReporterFinding): string {
  const m = findingMessage(f);
  return t(m.key, m.detailKey ? { ...m.vars, detail: t(m.detailKey) } : m.vars);
}

function Findings({ findings, t }: { findings: ReporterFinding[]; t: T }) {
  if (findings.length === 0) {
    return (
      <p className="crm-sub flex items-center gap-2 text-[13px]">
        <CircleCheck className="size-4 text-[var(--color-success)]" />
        {t("monitoring.reporter.noFindings")}
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-1.5">
      {findings.map((f) => (
        <li key={f.code} className="flex items-start gap-2.5 text-[13px]">
          <StatusPill tone={findingTone(f.level)} className="shrink-0">
            {t(`monitoring.reporter.level.${f.level}`)}
          </StatusPill>
          <span className="min-w-0 pt-0.5">{findingText(t, f)}</span>
        </li>
      ))}
    </ul>
  );
}

function Changes({ report, t }: { report: ReporterReportView; t: T }) {
  if (!report.diff) return <p className="crm-sub text-[13px]">{t("monitoring.reporter.firstReport")}</p>;
  const lines = diffLines(report.diff, fmtDateTime);
  if (lines.length === 0) return <p className="crm-sub text-[13px]">{t("monitoring.reporter.noChanges")}</p>;
  return (
    <ul className="flex flex-col gap-1 text-[13px]">
      {lines.map((line, i) => (
        <li key={`${line.key}-${i}`} className="flex items-start gap-2">
          {line.alarm ? (
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-[var(--color-destructive)]" strokeWidth={2} />
          ) : (
            <ArrowRight className="crm-mut mt-0.5 size-3.5 shrink-0" strokeWidth={2} />
          )}
          <span className="min-w-0 break-words">{t(line.key, line.vars)}</span>
        </li>
      ))}
    </ul>
  );
}

export function ReportSummary({ report, expect, t }: { report: ReporterReportView; expect: ReporterExpectView; t: T }) {
  return (
    <>
      <Figures report={report} t={t} />
      {showBackupBlock(report, expect) ? <BackupBlock report={report} expect={expect} t={t} /> : null}
      <div className="grid gap-6 px-[26px] pb-2 pt-5 lg:grid-cols-2">
        <div>
          <p className="crm-prow-n mb-2">{t("monitoring.reporter.findingsTitle")}</p>
          <Findings findings={report.findings} t={t} />
        </div>
        <div>
          <p className="crm-prow-n mb-2">{t("monitoring.reporter.changesTitle")}</p>
          <Changes report={report} t={t} />
        </div>
      </div>
    </>
  );
}
